import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import type { PrescriptionDetail } from '../../store/prescriptionsSlice'

interface PrescriptionDetailHeaderProps {
  prescription: PrescriptionDetail
  onUpdate: (data: Partial<PrescriptionDetail>) => Promise<void>
  onRefill: (data: { quantity_dispensed: number; dispensed_by: string; notes: string }) => Promise<void>
}

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Active':
      return 'bg-accent-subtle-bg text-status-success'
    case 'Completed':
      return 'bg-border text-text-secondary'
    case 'Discontinued':
      return 'bg-[#f0e8d0] text-status-warning'
    case 'Expired':
      return 'bg-[#f0dede] text-status-error'
    default:
      return 'bg-border text-text-muted'
  }
}

export function PrescriptionDetailHeader({ prescription, onUpdate, onRefill }: PrescriptionDetailHeaderProps) {
  const [editOpen, setEditOpen] = useState(false)
  const [refillOpen, setRefillOpen] = useState(false)

  const [editDosage, setEditDosage] = useState(prescription.dosage)
  const [editFrequency, setEditFrequency] = useState(prescription.frequency)
  const [editRoute, setEditRoute] = useState(prescription.route)
  const [editDuration, setEditDuration] = useState(prescription.duration)
  const [editQuantity, setEditQuantity] = useState(String(prescription.quantity_dispensed))
  const [editStartDate, setEditStartDate] = useState(prescription.start_date || '')
  const [editEndDate, setEditEndDate] = useState(prescription.end_date || '')

  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false)

  const [refillQuantity, setRefillQuantity] = useState('')
  const [refillDispensedBy, setRefillDispensedBy] = useState('')
  const [refillNotes, setRefillNotes] = useState('')

  const rxNumber = `RX-${String(prescription.prescription_number).padStart(4, '0')}`
  const isActive = prescription.status === 'Active'
  const canRefill = isActive && prescription.remaining_refills > 0

  const handleDiscontinue = async () => {
    if (window.confirm('Are you sure you want to discontinue this prescription?')) {
      await onUpdate({ status: 'Discontinued' })
    }
  }

  const [editError, setEditError] = useState('')

  const handleEditSave = async () => {
    if (!editDosage.trim()) {
      setEditError('Dosage is required')
      return
    }
    setEditError('')
    await onUpdate({
      dosage: editDosage,
      frequency: editFrequency,
      route: editRoute,
      duration: editDuration,
      quantity_dispensed: parseInt(editQuantity) || 0,
      start_date: editStartDate,
      end_date: editEndDate || null,
    })
    setEditOpen(false)
  }

  const handleRefillSubmit = async () => {
    await onRefill({
      quantity_dispensed: parseInt(refillQuantity) || 0,
      dispensed_by: refillDispensedBy,
      notes: refillNotes,
    })
    setRefillOpen(false)
    setRefillQuantity('')
    setRefillDispensedBy('')
    setRefillNotes('')
  }

  return (
    <div data-testid="prescription-detail-header">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h1
              className="text-[16px] font-semibold text-text-primary"
              data-testid="prescription-rx-number"
            >
              {rxNumber}
            </h1>
            <span
              data-testid="prescription-status-badge"
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(prescription.status)}`}
            >
              {prescription.status}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[13px] text-text-muted flex-wrap">
            <span>
              Patient:{' '}
              <Link
                to={`/patients/${prescription.patient_id}`}
                className="text-accent hover:text-accent-dark underline"
                data-testid="prescription-patient-link"
              >
                {prescription.patient_name}
              </Link>
            </span>
            {prescription.veterinarian_name && (
              <span data-testid="prescription-veterinarian">
                Prescribed by: {prescription.veterinarian_name}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isActive && (
            <button
              data-testid="edit-prescription-button"
              onClick={() => { setEditError(''); setEditOpen(true) }}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
            >
              Edit
            </button>
          )}
          {isActive && (
            <button
              data-testid="discontinue-prescription-button"
              onClick={handleDiscontinue}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-status-error text-white text-[13px] font-normal hover:opacity-80 transition-colors duration-100 cursor-pointer"
            >
              Discontinue
            </button>
          )}
          {canRefill && (
            <button
              data-testid="refill-prescription-button"
              onClick={() => setRefillOpen(true)}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent-light text-white text-[13px] font-normal hover:bg-accent transition-colors duration-100 cursor-pointer"
            >
              Refill
            </button>
          )}
        </div>
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="edit-prescription-modal">
          <div className="bg-surface rounded-[8px] shadow-elevation-2 w-full max-w-lg max-sm:max-w-[calc(100%-24px)] p-5 max-sm:p-4">
            <h2 className="text-[14px] font-semibold text-text-primary mb-4">Edit Prescription</h2>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Dosage
                <input
                  data-testid="edit-dosage-input"
                  value={editDosage}
                  onChange={(e) => setEditDosage(e.target.value)}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
              <div className="flex flex-col gap-1 text-[12px] text-text-muted">
                Frequency
                <div className="relative">
                  <button
                    type="button"
                    data-testid="edit-frequency-select"
                    onClick={() => setShowFrequencyDropdown(!showFrequencyDropdown)}
                    className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer flex items-center justify-between"
                  >
                    {editFrequency}
                    <ChevronDown size={12} className="text-text-muted" />
                  </button>
                  {showFrequencyDropdown && (
                    <div
                      data-testid="edit-frequency-dropdown"
                      className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                      style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                    >
                      {['SID', 'BID', 'TID', 'QID', 'q8h', 'q12h', 'PRN'].map((f) => (
                        <button
                          key={f}
                          type="button"
                          data-testid={`edit-frequency-option-${f}`}
                          onClick={() => {
                            setEditFrequency(f)
                            setShowFrequencyDropdown(false)
                          }}
                          className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Route
                <input
                  data-testid="edit-route-input"
                  value={editRoute}
                  onChange={(e) => setEditRoute(e.target.value)}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Duration
                <input
                  data-testid="edit-duration-input"
                  value={editDuration}
                  onChange={(e) => setEditDuration(e.target.value)}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Quantity Dispensed
                <input
                  data-testid="edit-quantity-input"
                  type="number"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
              <div className="flex gap-3">
                <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
                  Start Date
                  <input
                    data-testid="edit-start-date-input"
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                  />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
                  End Date
                  <input
                    data-testid="edit-end-date-input"
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                    className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                  />
                </label>
              </div>
            </div>
            {editError && (
              <p data-testid="edit-error-message" className="text-[12px] text-status-error mt-2">{editError}</p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                data-testid="edit-cancel-button"
                onClick={() => setEditOpen(false)}
                className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                data-testid="edit-save-button"
                onClick={handleEditSave}
                className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {refillOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="refill-modal">
          <div className="bg-surface rounded-[8px] shadow-elevation-2 w-full max-w-md max-sm:max-w-[calc(100%-24px)] p-5 max-sm:p-4">
            <h2 className="text-[14px] font-semibold text-text-primary mb-4">Process Refill</h2>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Quantity Dispensed
                <input
                  data-testid="refill-quantity-input"
                  type="number"
                  value={refillQuantity}
                  onChange={(e) => setRefillQuantity(e.target.value)}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Dispensed By
                <input
                  data-testid="refill-dispensed-by-input"
                  value={refillDispensedBy}
                  onChange={(e) => setRefillDispensedBy(e.target.value)}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Notes
                <input
                  data-testid="refill-notes-input"
                  value={refillNotes}
                  onChange={(e) => setRefillNotes(e.target.value)}
                  className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                data-testid="refill-cancel-button"
                onClick={() => setRefillOpen(false)}
                className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                data-testid="refill-submit-button"
                onClick={handleRefillSubmit}
                className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
              >
                Submit Refill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
